import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, Star, Check, Minus, ArrowRight, Search, BadgeCheck, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useTools } from '../hooks/useData';
import { useCompare } from '../context/CompareContext';

const ComparePage = () => {
  const { compareList, addToCompare, removeFromCompare } = useCompare();
  const { tools, loading } = useTools();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const filteredTools = useMemo(() => {
    if (!searchQuery) return tools;
    const query = searchQuery.toLowerCase();
    return tools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(query) ||
        tool.category.toLowerCase().includes(query)
    );
  }, [tools, searchQuery]);

  const handleAddTool = (tool) => {
    addToCompare(tool);
    setIsDialogOpen(false);
    setSearchQuery('');
  };

  const allFeatures = useMemo(() => {
    const features = new Set();
    compareList.forEach((tool) => {
      if (tool.features) {
        tool.features.forEach((feature) => features.add(feature));
      }
    });
    return Array.from(features);
  }, [compareList]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Compare Tools
            </h1>
            <p className="text-lg text-gray-600">
              Select up to 4 tools to compare features, pricing, and reviews side by side
            </p>
          </div>

          {/* Tool Selection Area */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              {compareList.map((tool) => (
                <div
                  key={tool.id}
                  className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 pr-2"
                >
                  <img
                    src={tool.logo}
                    alt={tool.name}
                    className="w-8 h-8 rounded-lg object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                    }}
                  />
                  <span className="font-medium text-gray-900">{tool.name}</span>
                  <button
                    onClick={() => removeFromCompare(tool.id)}
                    className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid={`remove-compare-${tool.slug}`}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}

              {compareList.length < 4 && (
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <button 
                      className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-emerald-300 hover:text-emerald-600 transition-colors"
                      data-testid="add-tool-to-compare-btn"
                    >
                      <Plus className="w-5 h-5" />
                      Add Tool
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Select a Tool to Compare</DialogTitle>
                    </DialogHeader>
                    <div className="mt-4">
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search tools..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                          data-testid="compare-search-input"
                        />
                      </div>
                      <div className="max-h-80 overflow-auto space-y-2">
                        {loading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                          </div>
                        ) : (
                          filteredTools
                            .filter((t) => !compareList.find((s) => s.id === t.id))
                            .map((tool) => (
                              <button
                                key={tool.id}
                                onClick={() => handleAddTool(tool)}
                                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
                                data-testid={`select-tool-${tool.slug}`}
                              >
                                <img
                                  src={tool.logo}
                                  alt={tool.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                                  }}
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {tool.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {tool.category}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 text-sm">
                                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                                  {tool.rating}
                                </div>
                              </button>
                            ))
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Comparison Table */}
          {compareList.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Header Row */}
              <div className="grid gap-px bg-gray-100" style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
                <div className="bg-gray-50 p-6">
                  <span className="font-semibold text-gray-700">Features</span>
                </div>
                {compareList.map((tool) => (
                  <div key={tool.id} className="bg-white p-6 text-center">
                    <img
                      src={tool.logo}
                      alt={tool.name}
                      className="w-14 h-14 rounded-xl object-cover mx-auto mb-3"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                      }}
                    />
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {tool.name}
                    </h3>
                    <div className="flex items-center justify-center gap-1 mb-2">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      <span className="text-sm font-medium">{tool.rating}</span>
                      <span className="text-xs text-gray-400">
                        ({(tool.reviews || 0).toLocaleString()})
                      </span>
                    </div>
                    {tool.verified && (
                      <Badge className="bg-emerald-50 text-emerald-600 border-0">
                        <BadgeCheck className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                ))}
              </div>

              {/* Pricing Row */}
              <div className="grid gap-px bg-gray-100" style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
                <div className="bg-gray-50 p-4 flex items-center">
                  <span className="text-sm font-medium text-gray-700">Pricing</span>
                </div>
                {compareList.map((tool) => (
                  <div key={tool.id} className="bg-white p-4 text-center">
                    <span className="font-semibold text-emerald-600">
                      {tool.pricing}
                    </span>
                  </div>
                ))}
              </div>

              {/* Category Row */}
              <div className="grid gap-px bg-gray-100" style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
                <div className="bg-gray-50 p-4 flex items-center">
                  <span className="text-sm font-medium text-gray-700">Category</span>
                </div>
                {compareList.map((tool) => (
                  <div key={tool.id} className="bg-white p-4 text-center">
                    <span className="text-sm text-gray-600">{tool.category}</span>
                  </div>
                ))}
              </div>

              {/* Features Rows */}
              {allFeatures.map((feature) => (
                <div key={feature} className="grid gap-px bg-gray-100" style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
                  <div className="bg-gray-50 p-4 flex items-center">
                    <span className="text-sm text-gray-700 line-clamp-2">{feature}</span>
                  </div>
                  {compareList.map((tool) => (
                    <div key={tool.id} className="bg-white p-4 text-center">
                      {tool.features && tool.features.includes(feature) ? (
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                          <Minus className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* View Details Row */}
              <div className="grid gap-px bg-gray-100" style={{ gridTemplateColumns: `200px repeat(${compareList.length}, 1fr)` }}>
                <div className="bg-gray-50 p-4" />
                {compareList.map((tool) => (
                  <div key={tool.id} className="bg-white p-4 text-center">
                    <Button asChild className="bg-emerald-500 hover:bg-emerald-600">
                      <Link to={`/tool/${tool.slug}`}>
                        View Details
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Plus className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No tools selected
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Add tools to start comparing features, pricing, and reviews side by side
              </p>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-first-tool-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Tool
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Select a Tool to Compare</DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search tools..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <div className="max-h-80 overflow-auto space-y-2">
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                        </div>
                      ) : (
                        filteredTools.map((tool) => (
                          <button
                            key={tool.id}
                            onClick={() => handleAddTool(tool)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
                          >
                            <img
                              src={tool.logo}
                              alt={tool.name}
                              className="w-10 h-10 rounded-lg object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                              }}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {tool.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {tool.category}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                              {tool.rating}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ComparePage;
